import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    crudStatusEnum,
    getCrudStatusLabel,
    getPriceTypeLabel,
    localStorageKeys,
    permissionLevelEnum,
    systemConfigKeyEnum,
} from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateToolTypeMutation } from '../../../../hooks/mutations/useToolTypeMutation';
import { useToolsTypesQuery } from '../../../../hooks/queries/useToolTypesQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { useTheme } from '../../../../hooks/useTheme';
import { ToolType } from '../../../../services/apiSLTP/materials/toolsTypes';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { formatNumber } from '../../../../utils/numbers';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateToolTypeModal from '../../../modals/admin/material/UpdateToolTypeModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface ToolsTypesTableProps {
    withUsageCount: boolean;
}

const ToolsTypesTable: React.FC<ToolsTypesTableProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedToolType, setSelectedToolType] = useState<ToolType | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();
    const { muiTheme } = useTheme();

    const { data, isLoading, refetch } = useToolsTypesQuery({
        queries: { withUsage: withUsageCount },
    });

    const { mutate: mutateToolType } = useUpdateToolTypeMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating toolType:', error.message),
    });

    const columnDefs: ColDef<ToolType>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<ToolType>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyToolType'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedToolType(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteToolType'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedToolType(params.data);
                                    setIsEditMode(false);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<ToolType>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            type: 'numericColumn',
            valueFormatter: (params) => formatNumber(params.value, t('locale')),
            field: 'price',
            cellStyle: (params) => (params.data?.price === 0 ? { color: muiTheme.palette.error.main } : undefined),
            colId: 'price',
            filter: 'agNumberColumnFilter',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeLabel(params.data?.priceType))),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('displayOrder')),
            field: 'displayOrder',
            colId: 'displayOrder',
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
        },
        createDateTimeColumn<ToolType>('createdAt', 'createdAt'),
        createDateTimeColumn<ToolType>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN)
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xTools')),
                field: 'usageCount.tools',
                colId: 'xtools',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );

    const sortedData = useMemo(() => {
        return data?.slice().sort((a, b) => a.designation.localeCompare(b.designation)) ?? [];
    }, [data]);

    return (
        <>
            <GenericAgGridTable
                rowData={sortedData.filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-toolTypes"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedToolType(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addToolType'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.MATERIAL.TOOLTYPES}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateToolTypeModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    toolType={selectedToolType}
                    isEditMode={isEditMode}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        if (selectedToolType) {
                            mutateToolType({
                                toolTypeId: selectedToolType._id,
                                data: { crudStatus: crudStatusEnum.DELETED },
                            });
                        }
                    }}
                    title={t('tool')}
                    itemName={selectedToolType?.designation}
                />
            )}
        </>
    );
};

export default ToolsTypesTable;
